// The keyboard's mode switch — the second of the two input switches.
// The ribbon has Free/Notes; the keyboard has Note/Chord/Harm, plus a row of
// options that changes with the mode. Sits between the ribbon and the keys.

import {
  ARP_DIR_LABELS,
  ARP_RATE_LABELS,
  CHORD_LABELS,
  NOTE_NAMES,
  arpEnabled,
  describe,
  type ArpDir,
  type ArpRate,
  type ChordChoice,
  type HarmonyMode,
  type HarmonySettings,
} from "../engine/harmony";
import { attachParamDrag } from "./paramDrag";
import type { ParamRef } from "./controls";

function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export interface HarmonyBarOptions {
  settings: HarmonySettings;
  /** Fired after any change; also the cue to release held notes. */
  onChange: () => void;
  /** Hand a param to the big right-edge slider (same contract as the rack). */
  onSelect?: (ref: ParamRef) => void;
}

export interface HarmonyBarController {
  render: () => void;
}

const MODES: ReadonlyArray<{ value: HarmonyMode; label: string; title: string }> = [
  { value: "note", label: "Note", title: "One key = one note (normal)" },
  { value: "chord", label: "Chord", title: "One key = a chord" },
  { value: "harm", label: "Harm", title: "One key = its natural overtones" },
];

export function buildHarmonyBar(container: HTMLElement, opts: HarmonyBarOptions): HarmonyBarController {
  container.classList.add("harmony-bar");
  const s = opts.settings;

  // A drag-chip, matching the app's one gesture for every parameter.
  function knob(
    parent: HTMLElement,
    label: string,
    min: number,
    max: number,
    step: number,
    get: () => number,
    set: (v: number) => void,
    fmt: (v: number) => string,
  ): void {
    const chip = el("button", "hknob");
    chip.setAttribute("type", "button");
    chip.title = `${label} — drag up/down`;
    const name = el("span");
    name.textContent = label;
    const val = el("b");
    const draw = (): void => {
      val.textContent = fmt(get());
    };
    chip.append(name, val);
    const ref: ParamRef = {
      label,
      min,
      max,
      step,
      scale: "lin",
      fmt,
      get,
      set: (v) => {
        set(v);
        draw();
        opts.onChange();
      },
    };
    attachParamDrag(chip, () => ref);
    draw();
    parent.append(chip);
  }

  // A labelled select, matching the Key/Chord fields already in this bar.
  function select(
    parent: HTMLElement,
    label: string,
    title: string,
    options: ReadonlyArray<{ value: string; label: string }>,
    current: string,
    pick: (v: string) => void,
  ): void {
    const sel = el("select", "hsel") as HTMLSelectElement;
    sel.title = title;
    for (const o of options) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.label;
      if (o.value === current) opt.selected = true;
      sel.append(opt);
    }
    sel.addEventListener("change", () => {
      pick(sel.value);
      opts.onChange();
      render();
    });
    const wrap = el("label", "hfield");
    const name = el("span");
    name.textContent = label;
    wrap.append(name, sel);
    parent.append(wrap);
  }

  // An on/off pill, same look as the mode switch (as "Odd only" already uses).
  function toggle(parent: HTMLElement, label: string, title: string, on: boolean, flip: () => void): void {
    const b = el("button", "hmode" + (on ? " on" : ""));
    b.setAttribute("type", "button");
    b.textContent = label;
    b.title = title;
    b.addEventListener("click", () => {
      flip();
      opts.onChange();
      render();
    });
    parent.append(b);
  }

  // Strum and arpeggio are alternatives: one fires the notes once, the other
  // keeps repeating them. Show whichever is actually in charge.
  function timing(row: HTMLElement): void {
    select(row, "Arp", "Arpeggio — repeat the notes in time with the BPM for as long as the key is held",
      ARP_RATE_LABELS, s.arp, (v) => { s.arp = v as ArpRate; });
    if (arpEnabled(s)) {
      select(row, "Dir", "Which way the arpeggio walks the notes",
        ARP_DIR_LABELS, s.arpDir, (v) => { s.arpDir = v as ArpDir; });
    } else {
      knob(row, "Strum", 0, 120, 5, () => s.strumMs, (v) => { s.strumMs = Math.round(v); },
        (v) => `${Math.round(v)}ms`);
    }
  }

  function render(): void {
    container.innerHTML = "";

    const modes = el("div", "hmodes");
    for (const m of MODES) {
      const b = el("button", "hmode" + (s.mode === m.value ? " on" : ""));
      b.setAttribute("type", "button");
      b.textContent = m.label;
      b.title = m.title;
      b.addEventListener("click", () => {
        s.mode = m.value;
        opts.onChange();
        render();
      });
      modes.append(b);
    }
    container.append(modes);

    const row = el("div", "hopts");
    container.append(row);

    if (s.mode === "chord") {
      const usesKey = s.chord.startsWith("scale:");

      // Key centre — only meaningful for the in-key scales.
      const keySel = el("select", "hsel") as HTMLSelectElement;
      keySel.title = "Key centre — which note the scale is built on";
      NOTE_NAMES.forEach((n, i) => {
        const o = document.createElement("option");
        o.value = String(i);
        o.textContent = n;
        if (i === s.root) o.selected = true;
        keySel.append(o);
      });
      keySel.addEventListener("change", () => {
        s.root = Number(keySel.value);
        opts.onChange();
        render();
      });
      keySel.disabled = !usesKey;

      const keyWrap = el("label", "hfield" + (usesKey ? "" : " is-off"));
      const keyName = el("span");
      keyName.textContent = "Key";
      keyWrap.append(keyName, keySel);
      row.append(keyWrap);

      // Chord picker — in-key scales and fixed shapes in one list.
      const chordSel = el("select", "hsel") as HTMLSelectElement;
      chordSel.title = "In-key scales auto-pick the right chord; fixed shapes stack the same one everywhere";
      let group: HTMLOptGroupElement | null = null;
      let groupName = "";
      for (const c of CHORD_LABELS) {
        if (c.group !== groupName) {
          groupName = c.group;
          group = document.createElement("optgroup");
          group.label = c.group;
          chordSel.append(group);
        }
        const o = document.createElement("option");
        o.value = c.value;
        o.textContent = c.label;
        if (c.value === s.chord) o.selected = true;
        group!.append(o);
      }
      chordSel.addEventListener("change", () => {
        s.chord = chordSel.value as ChordChoice;
        opts.onChange();
        render();
      });
      const chordWrap = el("label", "hfield");
      const chordName = el("span");
      chordName.textContent = "Chord";
      chordWrap.append(chordName, chordSel);
      row.append(chordWrap);

      // Only the in-key scales can build a *correct* seventh per degree; the
      // fixed shapes already have their own 7th entry.
      if (usesKey) {
        toggle(row, "7th", "Add the seventh — the scale picks the right one for each step", s.seventh,
          () => { s.seventh = !s.seventh; });
      }

      knob(row, "Shift", -2, 2, 1, () => s.shift, (v) => { s.shift = Math.round(v); },
        (v) => (v > 0 ? `+${Math.round(v)}` : String(Math.round(v))));
      timing(row);
    } else if (s.mode === "harm") {
      knob(row, "Partials", 2, 8, 1, () => s.partials, (v) => { s.partials = Math.round(v); },
        (v) => String(Math.round(v)));

      toggle(row, "Odd only", "Skip even overtones — hollow, clarinet-like", s.oddOnly,
        () => { s.oddOnly = !s.oddOnly; });

      timing(row);
    }

    const readout = el("span", "hreadout");
    readout.textContent = describe(s);
    container.append(readout);
  }

  render();
  return { render };
}
